/**
 * QuickActionsPanel — paste-a-UUID one-shot founder actions.
 *
 * Designed for the common case where a founder/admin is working from a
 * report or a Slack ping that already includes a target user ID — they
 * shouldn't need to search by username first. All actions are audited
 * server-side via founder-admin-action.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useFounderAdmin, type AdminKind } from "@/lib/founder/useFounderAdmin";
import { toast } from "@/hooks/use-toast";
import { Zap } from "lucide-react";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ACTIONS: { value: AdminKind; label: string; needsReason: boolean; danger?: boolean }[] = [
  { value: "warn_user",            label: "Warn user",           needsReason: true  },
  { value: "mark_test_account",    label: "Mark test account",   needsReason: false },
  { value: "unmark_test_account",  label: "Unmark test account", needsReason: false },
  { value: "block_user",           label: "Block user",          needsReason: true, danger: true },
  { value: "unblock_user",         label: "Unblock user",        needsReason: false },
];

export const QuickActionsPanel = () => {
  const [targetId, setTargetId] = useState("");
  const [kind, setKind] = useState<AdminKind>("warn_user");
  const [reason, setReason] = useState("");
  const { run, busy } = useFounderAdmin();

  const action = ACTIONS.find((a) => a.value === kind)!;
  const validUuid = UUID_RE.test(targetId.trim());
  const ready = validUuid && (!action.needsReason || reason.trim().length >= 4);

  const submit = async () => {
    if (!ready) return;
    if (action.danger && !confirm(`Run "${action.label}" against ${targetId.slice(0, 8)}…?`)) {
      return;
    }
    const res = await run({
      kind,
      target_user_id: targetId.trim(),
      reason: reason.trim() || undefined,
    });
    if (res.ok) {
      toast({ title: `${action.label} applied`, description: targetId.slice(0, 12) + "…" });
      setReason("");
    } else {
      toast({
        title: "Action failed",
        description: res.error ?? "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4" />
        <h3 className="text-sm font-medium">Quick actions</h3>
        <Badge variant="outline" className="ml-auto">paste user ID</Badge>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Skip the search step when you already have a user UUID. Every action is
        logged in the audit log below.
      </p>

      <div className="grid gap-3 sm:grid-cols-[1fr_220px]">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Target user ID</label>
          <Input
            placeholder="00000000-0000-0000-0000-000000000000"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            className={
              targetId && !validUuid
                ? "border-destructive focus-visible:ring-destructive"
                : ""
            }
          />
          {targetId && !validUuid && (
            <p className="text-xs text-destructive mt-1">Not a valid UUID.</p>
          )}
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Action</label>
          <Select value={kind} onValueChange={(v) => setKind(v as AdminKind)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {action.needsReason && (
        <div>
          <label className="text-xs text-muted-foreground block mb-1">
            Reason <span className="text-destructive">*</span>
          </label>
          <Textarea
            rows={2}
            placeholder="Visible in audit log. Be specific."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={submit} disabled={!ready || busy} variant={action.danger ? "destructive" : "default"}>
          {busy ? "Working…" : action.label}
        </Button>
      </div>
    </Card>
  );
};

export default QuickActionsPanel;
